import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";

// Never opens the agent WebSocket nor calls `/connectors/pair`: that's the clinic-side agent's job.

/** Mirrors brian's `ConnectorStatus` (liveness folded with the auto-update log server-side). */
export type ConnectorStatus = components["schemas"]["ConnectorStatus"];

/** Exhaustive: a new brian `ConnectorStatus` fails the build here and at `STATUS_WORD`. */
const KNOWN_STATUS: Record<ConnectorStatus, true> = {
  connected: true,
  updating: true,
  offline: true,
};

const isKnownStatus = (value: unknown): value is ConnectorStatus =>
  typeof value === "string" && value in KNOWN_STATUS;

/** What one update cycle did. Mirrors brian's `ConnectorUpdateOutcome`. */
export type ConnectorUpdateOutcome =
  components["schemas"]["ConnectorUpdateOutcome"];

interface UpdateEvent {
  uuid: string;
  outcome: ConnectorUpdateOutcome;
  fromVersion: string | null;
  toVersion: string | null;
  detail: string;
  occurredAt: string;
}

export interface Connector {
  id: string;
  name: string | null;
  hostname: string;
  agentVersion: string;
  connected: boolean;
  status: ConnectorStatus;
  lastUpdate: UpdateEvent | null;
}

interface MintedCode {
  code: string;
  ttlSeconds: number;
}

const toUpdateEvent = (
  u: components["schemas"]["UpdateEventSummary"],
): UpdateEvent => ({
  uuid: u.uuid,
  outcome: u.outcome,
  fromVersion: u.from_version ?? null,
  toVersion: u.to_version ?? null,
  detail: u.detail,
  occurredAt: u.occurred_at,
});

const toConnector = (
  c: components["schemas"]["ConnectorSummary"],
): Connector => ({
  id: c.uuid,
  name: c.name ?? null,
  hostname: c.hostname,
  agentVersion: c.agent_version,
  connected: c.connected,
  // Deploy skew can send an absent or unknown status: fall back to the binary `connected`,
  // never a guessed `updating` (the abandoned-update age-out is server-side).
  status: isKnownStatus(c.status)
    ? c.status
    : c.connected
      ? "connected"
      : "offline",
  lastUpdate: c.last_update ? toUpdateEvent(c.last_update) : null,
});

const toMintedCode = (
  m: components["schemas"]["MintCodeResponse"],
): MintedCode => ({
  code: m.code,
  ttlSeconds: m.ttl_seconds,
});

export const connectorClient = {
  mintPairingCode: (name?: string) =>
    request(() =>
      apiClient.POST("/connectors/pair/code", {
        body: { name: name ?? null },
      }),
    ).then(toMintedCode),

  listConnectors: () =>
    request(() => apiClient.GET("/connectors", {})).then((rows) =>
      rows.map(toConnector),
    ),

  /** Closes any live socket and deletes the row. */
  unpair: (connectorId: string) =>
    requestVoid(() =>
      apiClient.DELETE("/connectors/{connector_uuid}", {
        params: { path: { connector_uuid: connectorId } },
      }),
    ),
};
